import { useCallback, useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { useAppDispatch, useAppSelector } from '@/modules/app/store'
import { openModal } from '@/modules/global-modal/globalModalSlice'
import { GlobalModalKey } from '@/modules/global-modal/types'
import { selectUserIsLogin } from '@/modules/user/userSlice'

import type { GamePlatform } from './contants'
import type { GameListItem } from './gameInterface'
import { initFreeTryMode, switchShowTips } from './gameSlice'
import { getGamePlatform, isInhousePlatform } from './utils'

interface Checker {
  predicate: (game: GameListItem) => boolean
  failCallback: (game: GameListItem) => void
}

export function useNavigateToGame() {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const isLogin = useAppSelector(selectUserIsLogin)

  // 需要登录
  const needLoginChecker: Checker = useMemo(
    () => ({
      predicate: () => !!isLogin,
      failCallback: () => dispatch(openModal({ key: GlobalModalKey.Login })),
    }),
    [dispatch, isLogin],
  )

  // 维护中
  const underMaintChecker: Checker = useMemo(
    () => ({
      predicate: (game: GameListItem) => game?.is_under_maint !== 1,
      failCallback: game =>
        dispatch(switchShowTips(game?.maintenance_text || '')),
    }),
    [dispatch],
  )

  const location = useLocation()

  const navigateToGame = useCallback(
    (game: GameListItem) => {
      const platform = getGamePlatform(game)
      const checkers: Checker[] = [underMaintChecker]

      if (!isInhousePlatform(platform) && !game?.try_play_switch) {
        checkers.push(needLoginChecker)
      }

      for (const checker of checkers) {
        if (!checker.predicate(game)) {
          checker.failCallback(game)
          return
        }
      }

      if (game?.try_play_switch) dispatch(initFreeTryMode())
      navigate(getGamePageUrl(game), {
        state: {
          from: location.pathname + location.search + location.hash,
        },
      })
    },
    [
      underMaintChecker,
      dispatch,
      navigate,
      location.pathname,
      location.search,
      location.hash,
      needLoginChecker,
    ],
  )
  return navigateToGame
}

function getGamePageUrl(game: GameListItem) {
  const platform = getGamePlatform(game)
  const PLATFORM_TO_LABEL_MAP: Record<GamePlatform, string> = {
    jili: 'jili',
    facai: 'facai',
    slotegrator: 'slot',
    evoplay: 'slot',
    hillingo: 'self',
    inhouse: 'inhouse',
    live_casino: 'live',
    pgsoft: 'pgsoft',
    fundist: 'fundist',
  }
  const label = PLATFORM_TO_LABEL_MAP[platform] ?? ''
  const { expand, game_id } = game
  const gameId = isInhousePlatform(platform) ? expand?.game_uuid : game_id
  return `/game/${label}/${gameId}`
}
